import React, { useMemo, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'

import {
    CircularProgress,
    TextField,
    InputAdornment,
    Chip,
    Button,
    Tooltip,
    IconButton,
    useTheme,
    Divider,
} from '@material-ui/core'
import {
    Search,
    FilterList,
    Edit,
    ViewModule,
    ViewColumn,
    Tune as TuneIcon,
    Add as AddIcon,
} from '@material-ui/icons'

import { useSchedulerSpreadsheet } from '../../../hooks/useSchedulerSpreadsheet'
import {
    _useFinderSelection,
    AptConfigController,
    AreaStatusTagController,
    useAppDispatch,
    usePrunedInfrastructure,
    useRootInfrastructure,
    useUpstreamServiceContract,
} from '../../../hooks'
import { SchedulerSpreadsheet, ColumnSchema } from './SchedulerSpreadsheet'
import { ColumnVisibilityModal } from './ColumnVisibilityModal'
import {
    BaseWorkorder,
    getUiStatus,
    ListVendor,
    ModelListMap,
    ModelMap,
    Schedule,
    getTotalPriceExpense,
    AreaConfig,
    Service,
    IdBoolMap,
    Area,
    convertListToMap,
    Unit,
    traverse,
} from '../../../models'
import { getServiceImageDetails } from '../../../models/WorkOrder/services'
import { hasPermission } from '../../../models/Users/services'
import {
    CREATE_WORKORDERS,
    SEE_VENDOR_FINANCES,
} from '../../../models/Permissions/types'
import { useUser } from '../../../hooks/useUser'
import { useTimezone } from '../../../contexts/timezone'
import { DateTime } from 'luxon'
import { VendorChangePopover } from './EditPopovers/VendorChangePopover'
import { DateEditPopover } from './EditPopovers/DateEditPopover'
import { StatusEditPopover } from './EditPopovers/StatusEditPopover'
import { CostEditPopover } from './EditPopovers/CostEditPopover'
import { ServiceAreaEditPopover } from './EditPopovers/ServiceAreaEditPopover'
import { CreateWorkorderPopover } from './EditPopovers/CreateWorkorderPopover'
import {
    CreateWorkOrderRequest,
    CreateWorkorderActionThunk,
    FinderLocationSelection,
    FinderSelectionMode,
    RootState,
    UnitRes,
    UpdateWorkOrderRequest,
    WorkorderDetailActionThunk,
    getPermissionRoleList,
    transitionWorkorderStatus,
} from '../../../store'
import { _useWorkorder } from '../../../hooks/_useWorkorder'
import { BulkEditSelectionList } from '../Drawers/BulkEditSelectionList'
import { ServiceDots } from '../../../components/ServiceDots/ServiceDots'
import { ScheduleFilterDrawer } from '../Drawers'
import { useScheduleFilter } from '../../../hooks/useFilter'
import { Container, InfrastructureFilterChips } from '../../../components'
import { SchedulerAssignModal } from '../Modals'

interface Props {
    schedule: Schedule
    astController: AreaStatusTagController
    aptConfigController: AptConfigController
    vendorList: ListVendor[]
    workorderList: BaseWorkorder[]
    unitWorkorderMap: ModelListMap<BaseWorkorder>
    areaConfigMap: ModelMap<AreaConfig>
    serviceList: Service[]
    updateWorkorder: (req: UpdateWorkOrderRequest) => WorkorderDetailActionThunk
    setIsSpreadsheetView: (value: boolean) => void
    createWorkorder: (req: CreateWorkOrderRequest) => CreateWorkorderActionThunk
}

export const SchedulerSpreadsheetHome = (props: Props) => {
    const {
        schedule,
        astController,
        aptConfigController,
        vendorList,
        workorderList,
        unitWorkorderMap,
        areaConfigMap,
        serviceList,
        updateWorkorder,
        setIsSpreadsheetView,
        createWorkorder,
    } = props

    const permissionRoleList = useSelector(
        (state: RootState) => state.permission.permissionRoleList,
    )

    const locationFinderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.RootSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'schedule',
    })

    const assignmentFinderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.PrunedSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'schedule',
    })

    const [vacantAreas, setVacantAreas] = useState<Area[]>([])
    const [serviceAreaMap, setServiceAreaMap] = useState<IdBoolMap>({})

    const timezoneState = useTimezone()
    const { workspaceUser } = useUser()
    const dispatch = useAppDispatch()

    React.useEffect(() => {
        dispatch(getPermissionRoleList({}))
    }, [])

    const { tree, unitList } = useRootInfrastructure(true)
    const root = tree.root

    const unitListMap = convertListToMap(unitList ?? [])

    const { areaStatusTagMap, bulkSwapAreaStatusTag } = astController

    const {
        unitConfigList,
        getUnitConfigMap,
        getInventoryConfigMap,
    } = aptConfigController
    const unitConfigMap = getUnitConfigMap()

    // Build required Model maps
    const filterController = useScheduleFilter(
        unitWorkorderMap,
        {},
        areaStatusTagMap,
        serviceAreaMap,
        areaConfigMap,
        unitConfigMap,
        vacantAreas,
        unitList ?? undefined,
    )

    const theme = useTheme()

    // Add global styles for better scrollbars and table headers
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Wider scrollbars for better usability */
            ::-webkit-scrollbar {
                width: 16px !important;
                height: 16px !important;
            }

            ::-webkit-scrollbar-track {
                background: #f1f1f1 !important;
                border-radius: 8px !important;
            }

            ::-webkit-scrollbar-thumb {
                background: #c1c1c1 !important;
                border-radius: 8px !important;
                border: 2px solid #f1f1f1 !important;
            }

            ::-webkit-scrollbar-thumb:hover {
                background: #a8a8a8 !important;
            }

            /* Force header cells to wrap text - more aggressive targeting */
            .rdt_TableHeadCell,
            .rdt_TableHeadCell *,
            [data-testid*="head"],
            [class*="head"],
            .rdt_TableHead div,
            .rdt_TableHead span,
            .rdt_TableHead * {
                white-space: normal !important;
                overflow: visible !important;
                text-overflow: clip !important;
                word-wrap: break-word !important;
                word-break: break-word !important;
                height: auto !important;
                min-height: auto !important;
                max-height: none !important;
            }

            /* Override any inline styles that prevent wrapping */
            [style*="white-space: nowrap"] {
                white-space: normal !important;
            }

            /* Make table headers stick below primary headers */
            .rdt_TableHead {
                position: sticky !important;
                top: 47px !important; /* Position below primary headers */
                z-index: 2 !important;
                background-color: #f5f5f5 !important;
            }

        `
        document.head.appendChild(style)

        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // Search and filtering state
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
    const searchInputRef = React.useRef<HTMLInputElement>(null)

    // Pagination state for performance
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(50) // Start with 50 rows for better performance

    const [scheduleFilterOpen, setScheduleFilterOpen] = useState(false)
    const [assignModalOpen, setAssignModalOpen] = useState(false)

    // Bulk edit state
    const [bulkEditMode, setBulkEditMode] = useState(false)
    const [selectedWorkorders, setSelectedWorkorders] = useState<
        ModelMap<BaseWorkorder>
    >({})
    const [areaSelection, setAreaSelection] = useState<IdBoolMap>({})

    // Vendor change popover state
    const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false)
    const [
        vendorPopoverAnchor,
        setVendorPopoverAnchor,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedWorkorder,
        setSelectedWorkorder,
    ] = useState<BaseWorkorder | null>(null)

    // Date edit popover state
    const [datePopoverOpen, setDatePopoverOpen] = useState(false)
    const [
        datePopoverAnchor,
        setDatePopoverAnchor,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedDateWorkorder,
        setSelectedDateWorkorder,
    ] = useState<BaseWorkorder | null>(null)

    // Status edit popover state
    const [statusPopoverOpen, setStatusPopoverOpen] = useState(false)
    const [
        statusPopoverAnchor,
        setStatusPopoverAnchor,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedStatusWorkorder,
        setSelectedStatusWorkorder,
    ] = useState<BaseWorkorder | null>(null)

    // Cost edit popover state
    const [costPopoverOpen, setCostPopoverOpen] = useState(false)
    const [
        costPopoverAnchor,
        setCostPopoverAnchor,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedCostWorkorder,
        setSelectedCostWorkorder,
    ] = useState<BaseWorkorder | null>(null)

    // Service area edit popover state
    const [serviceAreaPopoverOpen, setServiceAreaPopoverOpen] = useState(false)
    const [
        serviceAreaPopoverAnchor,
        setServiceAreaPopoverAnchor,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedServiceAreaWorkorder,
        setSelectedServiceAreaWorkorder,
    ] = useState<BaseWorkorder | null>(null)

    // Column visibility state
    const [
        columnVisibilityDialogOpen,
        setColumnVisibilityDialogOpen,
    ] = useState(false)
    const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
        // Load hidden columns from localStorage
        try {
            const saved = localStorage.getItem(
                `scheduler-columns-hidden-${schedule.id}`,
            )
            return saved ? new Set(JSON.parse(saved)) : new Set()
        } catch {
            return new Set()
        }
    })

    // ServiceDots popover state
    const [serviceDotsPopoverOpen, setServiceDotsPopoverOpen] = useState(false)
    const [
        serviceDotsPopoverAnchor,
        setServiceDotsPopoverAnchor,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedServiceDotsWorkorders,
        setSelectedServiceDotsWorkorders,
    ] = useState<BaseWorkorder[]>([])

    // Create workorder popover state
    const [
        createWorkorderPopoverOpen,
        setCreateWorkorderPopoverOpen,
    ] = useState(false)
    const [
        createWorkorderPopoverAnchor,
        setCreateWorkorderPopoverAnchor,
    ] = useState<HTMLElement | null>(null)
    const [
        selectedUnitForCreate,
        setSelectedUnitForCreate,
    ] = useState<Unit | null>(null)
    const [
        selectedServiceForCreate,
        setSelectedServiceForCreate,
    ] = useState<Service | null>(null)

    // Handle search application
    const handleApplySearch = useCallback(() => {
        const currentValue = searchInputRef.current?.value || ''
        setAppliedSearchTerm(currentValue)
    }, [])

    // Handle Enter key press
    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                handleApplySearch()
            }
        },
        [handleApplySearch],
    )

    // Clear search
    const handleClearSearch = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.value = ''
        }
        setAppliedSearchTerm('')
    }, [])

    // Use the custom hook to get data
    const {
        units,
        isLoading,
        error,
        maxBedrooms,
        bedroomNamingConvention,
        fetchUnits,
    } = useSchedulerSpreadsheet({
        scheduleId: schedule.id,
        autoFetch: true,
    })

    const upstreamContractMap = useUpstreamServiceContract()

    const prunedTree = usePrunedInfrastructure(
        tree,
        locationFinderSelection.selection,
        filterController.filter,
        filterController.isUnitInFilter,
    )

    // Filtered units based on applied search
    const filteredUnits = useMemo(() => {
        const unitMap: ModelMap<Unit> = {}

        traverse(prunedTree.root, (folder) => {
            folder.units.forEach((unit) => {
                unitMap[unit.id] = unit
            })
        })

        const searchLower = appliedSearchTerm.toLowerCase()
        return units.filter((unit) => {
            const infrastructureUnit = unitMap[unit.id]
            if (!infrastructureUnit) {
                return false
            }

            if (!filterController.isUnitInFilter(infrastructureUnit)) {
                return false
            }

            // Search in unit properties
            if (
                unit.unit_location?.toLowerCase().includes(searchLower) ||
                unit.unit_name?.toLowerCase().includes(searchLower) ||
                unit.unit_config_name?.toLowerCase().includes(searchLower)
            ) {
                return true
            }

            // Search in workorder properties using unitWorkorderMap
            const unitWorkorders = unitWorkorderMap[unit.id]
            if (Array.isArray(unitWorkorders) && unitWorkorders.length > 0) {
                return unitWorkorders.some(
                    (wo: BaseWorkorder) =>
                        wo.vendor_name?.toLowerCase().includes(searchLower) ||
                        wo.status?.toLowerCase().includes(searchLower) ||
                        wo.service_name?.toLowerCase().includes(searchLower),
                )
            }

            return false
        })
    }, [
        units,
        appliedSearchTerm,
        unitWorkorderMap,
        prunedTree.root,
        filterController,
    ])

    // Sorting state
    const [sortConfig, setSortConfig] = useState<{
        key: string
        direction: 'asc' | 'desc'
    } | null>(null)

    const serviceMap = useMemo(() => {
        return serviceList.reduce((acc, service) => {
            acc[service.id] = service
            return acc
        }, {} as Record<number, Service>)
    }, [serviceList])

    // Reset to first page when search changes or sorting changes
    React.useEffect(() => {
        setCurrentPage(1)
    }, [appliedSearchTerm, sortConfig])

    // Handle sorting
    const handleSort = useCallback((key: string) => {
        console.log('🎯 handleSort called with key:', key)
        setSortConfig((prev) => {
            if (prev?.key === key) {
                // Toggle direction if same column
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc'
                console.log('🔄 Toggling sort direction to:', newDirection)
                return {
                    key,
                    direction: newDirection,
                }
            } else {
                // New column, start with ascending
                console.log('🆕 New sort column, direction: asc')
                return {
                    key,
                    direction: 'asc',
                }
            }
        })
    }, [])

    // Handle vendor change
    const handleVendorChange = useCallback(
        async (workorder: BaseWorkorder, newVendorId: number) => {
            if (!selectedWorkorder) return

            try {
                const updateWoReq: UpdateWorkOrderRequest = {
                    workorderId: workorder.id,
                    body: {
                        vendor: newVendorId,
                    },
                }

                updateWorkorder(updateWoReq).then(() => {
                    toast.success('Vendor updated successfully')
                })
            } catch (error) {
                console.error('Failed to update vendor:', error)
                throw error
            }
        },
        [selectedWorkorder, unitWorkorderMap],
    )

    // Handle opening vendor change popover
    const handleVendorEditClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, workorder: BaseWorkorder) => {
            setSelectedWorkorder(workorder)
            setVendorPopoverAnchor(event.currentTarget)
            setVendorPopoverOpen(true)
        },
        [],
    )

    // Handle closing vendor change popover
    const handleVendorPopoverClose = useCallback(() => {
        setVendorPopoverOpen(false)
        setVendorPopoverAnchor(null)
        setSelectedWorkorder(null)
    }, [])

    // Handle date change
    const handleDateChange = useCallback(
        async (
            workorder: BaseWorkorder,
            startDate: string,
            endDate: string,
        ) => {
            try {
                const updateWoReq: UpdateWorkOrderRequest = {
                    workorderId: workorder.id,
                    body: {
                        start_date: startDate,
                        end_date: endDate,
                    },
                }

                updateWorkorder(updateWoReq).then(() => {
                    toast.success('Dates updated successfully')
                })
            } catch (error) {
                console.error('Failed to update dates:', error)
                throw error
            }
        },
        [unitWorkorderMap],
    )

    // Handle opening date edit popover
    const handleDateEditClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, workorder: BaseWorkorder) => {
            setSelectedDateWorkorder(workorder)
            setDatePopoverAnchor(event.currentTarget)
            setDatePopoverOpen(true)
        },
        [],
    )

    // Handle closing date edit popover
    const handleDatePopoverClose = useCallback(() => {
        setDatePopoverOpen(false)
        setDatePopoverAnchor(null)
        setSelectedDateWorkorder(null)
    }, [])

    // Handle status transition
    const handleStatusTransition = useCallback(
        async (workorder: BaseWorkorder, newStatus: string) => {
            try {
                const transitionReq = {
                    workorderId: workorder.id,
                    body: {
                        status: newStatus as any,
                    },
                }

                dispatch(transitionWorkorderStatus(transitionReq)).then(() => {
                    toast.success('Status updated successfully')

                    // Update the selected workorder with the new status so the popover reflects the change
                    if (
                        selectedStatusWorkorder &&
                        selectedStatusWorkorder.id === workorder.id
                    ) {
                        setSelectedStatusWorkorder({
                            ...selectedStatusWorkorder,
                            status: newStatus as any,
                        })
                    }
                })
            } catch (error) {
                console.error('Failed to update status:', error)
                throw error
            }
        },
        [selectedStatusWorkorder],
    )

    // Handle opening status edit popover
    const handleStatusEditClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, workorder: BaseWorkorder) => {
            setSelectedStatusWorkorder(workorder)
            setStatusPopoverAnchor(event.currentTarget)
            setStatusPopoverOpen(true)
        },
        [],
    )

    // Handle closing status edit popover
    const handleStatusPopoverClose = useCallback(() => {
        setStatusPopoverOpen(false)
        setStatusPopoverAnchor(null)
        setSelectedStatusWorkorder(null)
    }, [])

    // Handle cost change
    const handleCostChange = useCallback(
        async (
            workorder: BaseWorkorder,
            addPrice: number | undefined,
            customPrice: number | undefined,
        ) => {
            try {
                const body: any = {
                    add_price: addPrice,
                }

                // Only include custom_price if it has a value
                if (customPrice !== undefined) {
                    body.custom_price = customPrice
                }

                const updateWoReq: UpdateWorkOrderRequest = {
                    workorderId: workorder.id,
                    body,
                }

                updateWorkorder(updateWoReq).then(() => {
                    toast.success('Cost updated successfully')
                })
            } catch (error) {
                console.error('Failed to update cost:', error)
                throw error
            }
        },
        [updateWorkorder],
    )

    // Handle opening cost edit popover
    const handleCostEditClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, workorder: BaseWorkorder) => {
            setSelectedCostWorkorder(workorder)
            setCostPopoverAnchor(event.currentTarget)
            setCostPopoverOpen(true)
        },
        [],
    )

    // Handle closing cost edit popover
    const handleCostPopoverClose = useCallback(() => {
        setCostPopoverOpen(false)
        setCostPopoverAnchor(null)
        setSelectedCostWorkorder(null)
    }, [])

    // Handle service area change
    const handleServiceAreaChange = useCallback(
        async (workorder: BaseWorkorder, serviceAreas: number[]) => {
            try {
                const updateWoReq: UpdateWorkOrderRequest = {
                    workorderId: workorder.id,
                    body: {
                        service_areas: serviceAreas,
                    },
                }

                updateWorkorder(updateWoReq).then(() => {
                    toast.success('Service areas updated successfully')
                })
            } catch (error) {
                console.error('Failed to update service areas:', error)
                throw error
            }
        },
        [updateWorkorder],
    )

    // Handle opening service area edit popover
    const handleServiceAreaEditClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, workorder: BaseWorkorder) => {
            setSelectedServiceAreaWorkorder(workorder)
            setServiceAreaPopoverAnchor(event.currentTarget)
            setServiceAreaPopoverOpen(true)
        },
        [],
    )

    // Handle closing service area edit popover
    const handleServiceAreaPopoverClose = useCallback(() => {
        setServiceAreaPopoverOpen(false)
        setServiceAreaPopoverAnchor(null)
        setSelectedServiceAreaWorkorder(null)
    }, [])

    // Handle opening ServiceDots popover
    const handleServiceDotsPopoverOpen = useCallback(
        (event: React.MouseEvent<HTMLElement>, workorders: BaseWorkorder[]) => {
            setSelectedServiceDotsWorkorders(workorders)
            setServiceDotsPopoverAnchor(event.currentTarget)
            setServiceDotsPopoverOpen(true)
        },
        [],
    )

    // Handle closing ServiceDots popover
    const handleServiceDotsPopoverClose = useCallback(() => {
        setServiceDotsPopoverOpen(false)
        setServiceDotsPopoverAnchor(null)
        setSelectedServiceDotsWorkorders([])
    }, [])

    // Handle opening create workorder popover
    const handleCreateWorkorderClick = useCallback(
        (
            event: React.MouseEvent<HTMLElement>,
            unit: Unit,
            service: Service,
        ) => {
            setSelectedUnitForCreate(unit)
            setSelectedServiceForCreate(service)
            setCreateWorkorderPopoverAnchor(event.currentTarget)
            setCreateWorkorderPopoverOpen(true)
        },
        [],
    )

    // Handle closing create workorder popover
    const handleCreateWorkorderPopoverClose = useCallback(() => {
        setCreateWorkorderPopoverOpen(false)
        setCreateWorkorderPopoverAnchor(null)
        setSelectedUnitForCreate(null)
        setSelectedServiceForCreate(null)
    }, [])

    // Handle workorder selection in bulk edit mode
    const handleWorkorderSelection = useCallback(
        (workorder: BaseWorkorder) => {
            if (!bulkEditMode) return

            setSelectedWorkorders((prev) => {
                if (prev[workorder.id]) {
                    // If already selected, remove it
                    const newSelection = { ...prev }
                    delete newSelection[workorder.id]
                    return newSelection
                } else {
                    // If not selected, add it
                    return {
                        ...prev,
                        [workorder.id]: workorder,
                    }
                }
            })
        },
        [bulkEditMode],
    )

    // Column visibility functions
    const toggleColumnVisibility = useCallback(
        (columnKey: string) => {
            setHiddenColumns((prev) => {
                const newSet = new Set(prev)
                if (newSet.has(columnKey)) {
                    newSet.delete(columnKey)
                } else {
                    newSet.add(columnKey)
                }
                // Save to localStorage
                try {
                    localStorage.setItem(
                        `scheduler-columns-hidden-${schedule.id}`,
                        JSON.stringify(Array.from(newSet)),
                    )
                } catch {
                    // Ignore localStorage errors
                }
                return newSet
            })
        },
        [schedule.id],
    )

    const showAllColumns = useCallback(() => {
        setHiddenColumns(new Set())
        // Clear from localStorage
        try {
            localStorage.removeItem(`scheduler-columns-hidden-${schedule.id}`)
        } catch {
            // Ignore localStorage errors
        }
    }, [schedule.id])

    const openColumnVisibilityDialog = useCallback(() => {
        setColumnVisibilityDialogOpen(true)
    }, [])

    const closeColumnVisibilityDialog = useCallback(() => {
        setColumnVisibilityDialogOpen(false)
    }, [])

    // Add keyboard shortcuts
    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Ctrl/Cmd + Shift + C to open column visibility dialog
            if (
                (event.ctrlKey || event.metaKey) &&
                event.shiftKey &&
                event.key === 'C'
            ) {
                event.preventDefault()
                openColumnVisibilityDialog()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [openColumnVisibilityDialog])

    // Generate bedroom columns dynamically based on data and naming convention
    const generateBedroomColumns = useMemo(
        () => (
            maxBedrooms: number,
            namingConvention: 'letters' | 'numbers',
        ): ColumnSchema[] => {
            if (maxBedrooms === 0) return []

            const bedroomColumns: ColumnSchema[] = []

            for (let i = 0; i < maxBedrooms; i++) {
                let bedIdentifier: string
                let key: string
                let displayName: string

                if (namingConvention === 'letters') {
                    bedIdentifier = String.fromCharCode(65 + i) // A, B, C, D, E, F...
                    key = `bed_${bedIdentifier}_status_tag`
                    displayName = `Bed ${bedIdentifier}`
                } else {
                    bedIdentifier = String(i + 1) // 1, 2, 3, 4, 5, 6...
                    key = `bed_${bedIdentifier}_status_tag`
                    displayName = `Bed ${bedIdentifier}`
                }

                bedroomColumns.push({
                    key,
                    name: displayName,
                    type: 'custom',
                    sortable: true,
                    customRenderer: (value) => {
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    padding: '8px 8px 8px 12px',
                                    boxSizing: 'border-box',
                                }}
                            >
                                {!value ? (
                                    <span style={{ color: '#ccc' }}>-</span>
                                ) : (
                                    <span>{value}</span>
                                )}
                            </div>
                        )
                    },
                })
            }

            return bedroomColumns
        },
        [],
    )

    // Get unique services from unitWorkorderMap
    const uniqueServices = useMemo(() => {
        const services = new Set<string>()
        Object.values(unitWorkorderMap).forEach((workorders) => {
            if (Array.isArray(workorders)) {
                workorders.forEach((wo: BaseWorkorder) => {
                    if (wo.service_name) {
                        services.add(wo.service_name)
                    }
                })
            }
        })
        return Array.from(services).sort()
    }, [unitWorkorderMap])

    // Create optimized workorder lookup map for better performance
    const workorderLookup = useMemo(() => {
        const lookup = new Map()
        Object.entries(unitWorkorderMap).forEach(([unitId, workorders]) => {
            const serviceMap = new Map()
            if (Array.isArray(workorders)) {
                workorders.forEach((wo: BaseWorkorder) => {
                    const unit = (unitListMap[
                        Number(unitId)
                    ] as unknown) as Unit
                    if (
                        wo.service_name &&
                        unit &&
                        filterController.isWorkorderInFilter(wo, unit)
                    ) {
                        serviceMap.set(wo.service_name, wo)
                    }
                })
            }
            lookup.set(Number(unitId), serviceMap)
        })
        return lookup
    }, [unitWorkorderMap, filterController])

    // Memoized DateTime operations for better performance
    const dateTimeCache = useMemo(() => {
        const cache = new Map()
        const getCachedDateTime = (isoString: string, timezone: string) => {
            const cacheKey = `${isoString}_${timezone}`
            if (!cache.has(cacheKey)) {
                const dt = DateTime.fromISO(isoString).setZone(timezone)
                cache.set(cacheKey, dt)
            }
            return cache.get(cacheKey)
        }
        return { getCachedDateTime }
    }, [timezoneState.currentTimezone])

    // Define column schema for the current table
    const columnSchema: ColumnSchema[] = useMemo(
        () => [
            {
                key: 'unit_location',
                name: 'Location',
                type: 'custom' as const,
                sortable: true,
                customRenderer: (value: any, row: any) => {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                padding: '8px 8px 8px 12px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <span>{value || '-'}</span>
                        </div>
                    )
                },
            },
            {
                key: 'unit_name',
                name: 'Unit',
                type: 'custom' as const,
                sortable: true,
                customRenderer: (value: any, row: any) => {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                padding: '8px 8px 8px 12px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <span>{value || '-'}</span>
                        </div>
                    )
                },
            },
            {
                key: 'unit_config_name',
                name: 'Unit Type',
                type: 'custom' as const,
                sortable: true,
                customRenderer: (value: any, row: any) => {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                padding: '8px 8px 8px 12px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <span>{value || '-'}</span>
                        </div>
                    )
                },
            },
            {
                key: 'occupiable_areas_count',
                name: '# Beds',
                type: 'custom' as const,
                sortable: true,
                customRenderer: (value: any, row: any) => {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                padding: '8px 8px 8px 12px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <span>{value || '-'}</span>
                        </div>
                    )
                },
            },
            ...generateBedroomColumns(maxBedrooms, bedroomNamingConvention), // Dynamic bedroom columns
            {
                key: 'services',
                name: 'Services',
                type: 'custom' as const,
                sortable: false,
                customRenderer: (value: any, row: any) => {
                    const unitWorkorders = unitWorkorderMap[row.id] || []
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                padding: '8px 8px 8px 12px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <ServiceDots
                                workorders={unitWorkorders}
                                popOverAnchror={serviceDotsPopoverAnchor}
                                openPopover={(event, workorders) =>
                                    handleServiceDotsPopoverOpen(
                                        event,
                                        workorders,
                                    )
                                }
                            />
                        </div>
                    )
                },
            },

            // Add workorder columns for each service
            ...uniqueServices.flatMap((service) => [
                {
                    key: `${service.toLowerCase()}_assigned_to`,
                    name: 'Assigned To',
                    type: 'custom' as const,
                    sortable: true,
                    selector: (row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        return workorder?.vendor_name || ''
                    },
                    customRenderer: (value: any, row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)

                        if (!workorder) {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px 8px 8px 12px',
                                        boxSizing: 'border-box',
                                        cursor: 'pointer',
                                        backgroundColor: '#f8f9fa',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        const unit = (unitListMap[
                                            row.id
                                        ] as unknown) as Unit
                                        const serviceData = serviceList.find(
                                            (s) => s.name === service,
                                        )
                                        if (
                                            unit &&
                                            serviceData &&
                                            hasPermission(
                                                workspaceUser,
                                                CREATE_WORKORDERS,
                                            )
                                        ) {
                                            handleCreateWorkorderClick(
                                                e as any,
                                                unit,
                                                serviceData,
                                            )
                                        }
                                    }}
                                    onMouseEnter={(e) => {
                                        if (
                                            hasPermission(
                                                workspaceUser,
                                                CREATE_WORKORDERS,
                                            )
                                        ) {
                                            e.currentTarget.style.backgroundColor =
                                                '#e9ecef'
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            '#f8f9fa'
                                    }}
                                >
                                    <span
                                        style={{
                                            color: '#6c757d',
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        Click to create
                                    </span>
                                </div>
                            )
                        }

                        const isAssigned = workorder.status === 'ASSIGNED'
                        const isSelected = selectedWorkorders[workorder.id]
                        const showEditIcon = isAssigned && !bulkEditMode

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor:
                                        bulkEditMode && isSelected
                                            ? '#ffebee'
                                            : 'transparent',
                                    cursor: bulkEditMode
                                        ? 'pointer'
                                        : 'default',
                                    padding: '8px 8px 8px 12px',
                                    boxSizing: 'border-box',
                                }}
                                onClick={() =>
                                    bulkEditMode &&
                                    handleWorkorderSelection(workorder)
                                }
                                onMouseEnter={(e) => {
                                    if (showEditIcon) {
                                        e.currentTarget.style.cursor = 'pointer'
                                        // Show the edit pen when hovering over the entire cell
                                        const editIcon = e.currentTarget.querySelector(
                                            '[data-edit-icon]',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '1'
                                        }
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.cursor = bulkEditMode
                                        ? 'pointer'
                                        : 'default'
                                    // Hide the edit pen when leaving the cell
                                    const editIcon = e.currentTarget.querySelector(
                                        '[data-edit-icon]',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '0'
                                    }
                                }}
                            >
                                <span>{workorder.vendor_name || '-'}</span>
                                {showEditIcon && (
                                    <Edit
                                        data-edit-icon="true"
                                        style={{
                                            fontSize: '16px',
                                            color: '#1976d2',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            cursor: 'pointer',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleVendorEditClick(
                                                e as any,
                                                workorder,
                                            )
                                        }}
                                    />
                                )}
                            </div>
                        )
                    },
                },
                {
                    key: `${service.toLowerCase()}_start_date`,
                    name: 'Start Date',
                    type: 'custom' as const,
                    sortable: true,
                    selector: (row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder?.start_date) return null
                        const startDate = dateTimeCache.getCachedDateTime(
                            workorder.start_date,
                            timezoneState.currentTimezone,
                        )
                        return startDate.toJSDate()
                    },
                    customRenderer: (value: any, row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder?.start_date) {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px 8px 8px 12px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <span>-</span>
                                </div>
                            )
                        }

                        const startDate = dateTimeCache.getCachedDateTime(
                            workorder.start_date,
                            timezoneState.currentTimezone,
                        )

                        const isAssigned = workorder.status === 'ASSIGNED'
                        const isSelected = selectedWorkorders[workorder.id]
                        const showEditIcon = isAssigned && !bulkEditMode

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor:
                                        bulkEditMode && isSelected
                                            ? '#ffebee'
                                            : 'transparent',
                                    cursor: bulkEditMode
                                        ? 'pointer'
                                        : 'default',
                                    padding: '8px 8px 8px 12px',
                                    boxSizing: 'border-box',
                                }}
                                onClick={() =>
                                    bulkEditMode &&
                                    handleWorkorderSelection(workorder)
                                }
                                onMouseEnter={(e) => {
                                    if (showEditIcon) {
                                        e.currentTarget.style.cursor = 'pointer'
                                        // Show the edit pen when hovering over the entire cell
                                        const editIcon = e.currentTarget.querySelector(
                                            '[data-edit-icon]',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '1'
                                        }
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.cursor = bulkEditMode
                                        ? 'pointer'
                                        : 'default'
                                    // Hide the edit pen when leaving the cell
                                    const editIcon = e.currentTarget.querySelector(
                                        '[data-edit-icon]',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '0'
                                    }
                                }}
                            >
                                <span>
                                    {startDate.toLocaleString(
                                        DateTime.DATE_SHORT,
                                    )}
                                </span>
                                {showEditIcon && (
                                    <Edit
                                        data-edit-icon="true"
                                        style={{
                                            fontSize: '16px',
                                            color: '#1976d2',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            cursor: 'pointer',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleDateEditClick(
                                                e as any,
                                                workorder,
                                            )
                                        }}
                                    />
                                )}
                            </div>
                        )
                    },
                },
                {
                    key: `${service.toLowerCase()}_end_date`,
                    name: 'End Date',
                    type: 'custom' as const,
                    sortable: true,
                    selector: (row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder?.end_date) return null
                        const endDate = dateTimeCache.getCachedDateTime(
                            workorder.end_date,
                            timezoneState.currentTimezone,
                        )
                        return endDate.toJSDate()
                    },
                    customRenderer: (value: any, row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder?.end_date) {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px 8px 8px 12px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <span>-</span>
                                </div>
                            )
                        }

                        const endDate = dateTimeCache.getCachedDateTime(
                            workorder.end_date,
                            timezoneState.currentTimezone,
                        )

                        const isAssigned = workorder.status === 'ASSIGNED'
                        const isSelected = selectedWorkorders[workorder.id]
                        const showEditIcon = isAssigned && !bulkEditMode

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor:
                                        bulkEditMode && isSelected
                                            ? '#ffebee'
                                            : 'transparent',
                                    cursor: bulkEditMode
                                        ? 'pointer'
                                        : 'default',
                                    padding: '8px 8px 8px 12px',
                                    boxSizing: 'border-box',
                                }}
                                onClick={() =>
                                    bulkEditMode &&
                                    handleWorkorderSelection(workorder)
                                }
                                onMouseEnter={(e) => {
                                    if (showEditIcon) {
                                        e.currentTarget.style.cursor = 'pointer'
                                        // Show the edit pen when hovering over the entire cell
                                        const editIcon = e.currentTarget.querySelector(
                                            '[data-edit-icon]',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '1'
                                        }
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.cursor = bulkEditMode
                                        ? 'pointer'
                                        : 'default'
                                    // Hide the edit pen when leaving the cell
                                    const editIcon = e.currentTarget.querySelector(
                                        '[data-edit-icon]',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '0'
                                    }
                                }}
                            >
                                <span>
                                    {endDate.toLocaleString(
                                        DateTime.DATE_SHORT,
                                    )}
                                </span>
                                {showEditIcon && (
                                    <Edit
                                        data-edit-icon="true"
                                        style={{
                                            fontSize: '16px',
                                            color: '#1976d2',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            cursor: 'pointer',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleDateEditClick(
                                                e as any,
                                                workorder,
                                            )
                                        }}
                                    />
                                )}
                            </div>
                        )
                    },
                },
                {
                    key: `${service.toLowerCase()}_created_date`,
                    name: 'Created Date',
                    type: 'custom' as const,
                    sortable: true,
                    selector: (row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder?.created_date) return null
                        const createdDate = dateTimeCache.getCachedDateTime(
                            workorder.created_date,
                            timezoneState.currentTimezone,
                        )
                        return createdDate.toJSDate()
                    },
                    customRenderer: (value: any, row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder?.created_date) {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px 8px 8px 12px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <span>-</span>
                                </div>
                            )
                        }
                        const createdDate = dateTimeCache.getCachedDateTime(
                            workorder.created_date,
                            timezoneState.currentTimezone,
                        )

                        const isAssigned = workorder.status === 'ASSIGNED'
                        const isSelected = selectedWorkorders[workorder.id]
                        const showEditIcon = isAssigned && !bulkEditMode

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor:
                                        bulkEditMode && isSelected
                                            ? '#ffebee'
                                            : 'transparent',
                                    cursor: bulkEditMode
                                        ? 'pointer'
                                        : 'default',
                                    padding: '8px 8px 8px 12px',
                                    boxSizing: 'border-box',
                                }}
                                onClick={() =>
                                    bulkEditMode &&
                                    handleWorkorderSelection(workorder)
                                }
                                onMouseEnter={(e) => {
                                    if (showEditIcon) {
                                        e.currentTarget.style.cursor = 'pointer'
                                        // Show the edit pen when hovering over the entire cell
                                        const editIcon = e.currentTarget.querySelector(
                                            '[data-edit-icon]',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '1'
                                        }
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.cursor = bulkEditMode
                                        ? 'pointer'
                                        : 'default'
                                    // Hide the edit pen when leaving the cell
                                    const editIcon = e.currentTarget.querySelector(
                                        '[data-edit-icon]',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '0'
                                    }
                                }}
                            >
                                <span>
                                    {createdDate.toLocaleString(
                                        DateTime.DATE_SHORT,
                                    )}
                                </span>
                                {showEditIcon && (
                                    <Edit
                                        data-edit-icon="true"
                                        style={{
                                            fontSize: '16px',
                                            color: '#1976d2',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            cursor: 'pointer',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleDateEditClick(
                                                e as any,
                                                workorder,
                                            )
                                        }}
                                    />
                                )}
                            </div>
                        )
                    },
                },
                {
                    key: `${service.toLowerCase()}_status`,
                    name: 'Status',
                    type: 'custom' as const,
                    sortable: true,
                    selector: (row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        return workorder?.status || ''
                    },
                    customRenderer: (value: any, row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder) {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px 8px 8px 12px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <span>-</span>
                                </div>
                            )
                        }

                        const isSelected = selectedWorkorders[workorder.id]
                        const showEditIcon = !bulkEditMode

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor:
                                        bulkEditMode && isSelected
                                            ? '#ffebee'
                                            : 'transparent',
                                    cursor: bulkEditMode
                                        ? 'pointer'
                                        : 'default',
                                    padding: '8px 8px 8px 12px',
                                    boxSizing: 'border-box',
                                }}
                                onClick={() =>
                                    bulkEditMode &&
                                    handleWorkorderSelection(workorder)
                                }
                                onMouseEnter={(e) => {
                                    if (showEditIcon) {
                                        e.currentTarget.style.cursor = 'pointer'
                                        // Show the edit pen when hovering over the entire cell
                                        const editIcon = e.currentTarget.querySelector(
                                            '[data-edit-icon]',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '1'
                                        }
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.cursor = bulkEditMode
                                        ? 'pointer'
                                        : 'default'
                                    // Hide the edit pen when leaving the cell
                                    const editIcon = e.currentTarget.querySelector(
                                        '[data-edit-icon]',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '0'
                                    }
                                }}
                            >
                                <span>{getUiStatus(workorder.status)}</span>
                                {showEditIcon && (
                                    <Edit
                                        data-edit-icon="true"
                                        style={{
                                            fontSize: '16px',
                                            color: '#1976d2',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            cursor: 'pointer',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleStatusEditClick(
                                                e as any,
                                                workorder,
                                            )
                                        }}
                                    />
                                )}
                            </div>
                        )
                    },
                },
                ...(hasPermission(workspaceUser, SEE_VENDOR_FINANCES)
                    ? [
                          {
                              key: `${service.toLowerCase()}_cost`,
                              name: 'Cost',
                              type: 'custom' as const,
                              sortable: true,
                              selector: (row: any) => {
                                  const workorder = workorderLookup
                                      .get(row.id)
                                      ?.get(service)
                                  if (!workorder) return 0
                                  return getTotalPriceExpense(workorder)
                              },
                              customRenderer: (value: any, row: any) => {
                                  const workorder = workorderLookup
                                      .get(row.id)
                                      ?.get(service)
                                  if (!workorder) {
                                      return (
                                          <div
                                              style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  position: 'relative',
                                                  width: '100%',
                                                  height: '100%',
                                                  padding: '8px 8px 8px 12px',
                                                  boxSizing: 'border-box',
                                              }}
                                          >
                                              <span>-</span>
                                          </div>
                                      )
                                  }

                                  const cost = getTotalPriceExpense(workorder)
                                  if (cost === -1) {
                                      return (
                                          <div
                                              style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  position: 'relative',
                                                  width: '100%',
                                                  height: '100%',
                                                  padding: '8px 8px 8px 12px',
                                                  boxSizing: 'border-box',
                                              }}
                                          >
                                              <span>-</span>
                                          </div>
                                      )
                                  }

                                  const isAssigned =
                                      workorder.status === 'ASSIGNED'
                                  const isSelected =
                                      selectedWorkorders[workorder.id]
                                  const showEditIcon =
                                      isAssigned && !bulkEditMode

                                  return (
                                      <div
                                          style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '8px',
                                              position: 'relative',
                                              width: '100%',
                                              height: '100%',
                                              backgroundColor:
                                                  bulkEditMode && isSelected
                                                      ? '#ffebee'
                                                      : 'transparent',
                                              cursor: bulkEditMode
                                                  ? 'pointer'
                                                  : 'default',
                                              padding: '8px 8px 8px 12px',
                                              boxSizing: 'border-box',
                                          }}
                                          onClick={() =>
                                              bulkEditMode &&
                                              handleWorkorderSelection(
                                                  workorder,
                                              )
                                          }
                                          onMouseEnter={(e) => {
                                              if (showEditIcon) {
                                                  e.currentTarget.style.cursor =
                                                      'pointer'
                                                  // Show the edit pen when hovering over the entire cell
                                                  const editIcon = e.currentTarget.querySelector(
                                                      '[data-edit-icon]',
                                                  ) as HTMLElement
                                                  if (editIcon) {
                                                      editIcon.style.opacity =
                                                          '1'
                                                  }
                                              }
                                          }}
                                          onMouseLeave={(e) => {
                                              e.currentTarget.style.cursor = bulkEditMode
                                                  ? 'pointer'
                                                  : 'default'
                                              // Hide the edit pen when leaving the cell
                                              const editIcon = e.currentTarget.querySelector(
                                                  '[data-edit-icon]',
                                              ) as HTMLElement
                                              if (editIcon) {
                                                  editIcon.style.opacity = '0'
                                              }
                                          }}
                                      >
                                          <span>${cost.toFixed(2)}</span>
                                          {showEditIcon && (
                                              <Edit
                                                  data-edit-icon="true"
                                                  style={{
                                                      fontSize: '16px',
                                                      color: '#1976d2',
                                                      opacity: 0,
                                                      transition:
                                                          'opacity 0.2s',
                                                      cursor: 'pointer',
                                                  }}
                                                  onClick={(e) => {
                                                      e.stopPropagation()
                                                      handleCostEditClick(
                                                          e as any,
                                                          workorder,
                                                      )
                                                  }}
                                              />
                                          )}
                                      </div>
                                  )
                              },
                          },
                      ]
                    : []),
                {
                    key: `${service.toLowerCase()}_service_areas`,
                    name: 'Service Areas',
                    type: 'custom' as const,
                    sortable: true,
                    selector: (row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        return workorder?.service_area_list?.length || 0
                    },
                    customRenderer: (value: any, row: any) => {
                        const workorder = workorderLookup
                            .get(row.id)
                            ?.get(service)
                        if (!workorder?.service_area_list?.length) {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px 8px 8px 12px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <span>-</span>
                                </div>
                            )
                        }

                        const isAssigned = workorder.status === 'ASSIGNED'
                        const isSelected = selectedWorkorders[workorder.id]
                        const showEditIcon = isAssigned && !bulkEditMode

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor:
                                        bulkEditMode && isSelected
                                            ? '#ffebee'
                                            : 'transparent',
                                    cursor: bulkEditMode
                                        ? 'pointer'
                                        : 'default',
                                    padding: '8px 8px 8px 12px',
                                    boxSizing: 'border-box',
                                }}
                                onClick={() =>
                                    bulkEditMode &&
                                    handleWorkorderSelection(workorder)
                                }
                                onMouseEnter={(e) => {
                                    if (showEditIcon) {
                                        e.currentTarget.style.cursor = 'pointer'
                                        // Show the edit pen when hovering over the entire cell
                                        const editIcon = e.currentTarget.querySelector(
                                            '[data-edit-icon]',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '1'
                                        }
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.cursor = bulkEditMode
                                        ? 'pointer'
                                        : 'default'
                                    // Hide the edit pen when leaving the cell
                                    const editIcon = e.currentTarget.querySelector(
                                        '[data-edit-icon]',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '0'
                                    }
                                }}
                            >
                                <span>
                                    {workorder.service_area_list.length}
                                </span>
                                {showEditIcon && (
                                    <Edit
                                        data-edit-icon="true"
                                        style={{
                                            fontSize: '16px',
                                            color: '#1976d2',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            cursor: 'pointer',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleServiceAreaEditClick(
                                                e as any,
                                                workorder,
                                            )
                                        }}
                                    />
                                )}
                            </div>
                        )
                    },
                },
            ]),
        ],
        [
            uniqueServices,
            maxBedrooms,
            timezoneState.currentTimezone,
            workorderLookup,
            dateTimeCache,
            bulkEditMode,
            selectedWorkorders,
            handleWorkorderSelection,
        ],
    )

    // Filter columns based on visibility settings
    const filteredColumnSchema = useMemo(() => {
        return columnSchema.filter((column) => !hiddenColumns.has(column.key))
    }, [columnSchema, hiddenColumns])

    // Sorted and filtered data
    const sortedAndFilteredUnits = useMemo(() => {
        const sorted = [...filteredUnits]

        if (sortConfig) {
            console.log(
                '🔄 Sorting by:',
                sortConfig.key,
                'Direction:',
                sortConfig.direction,
            )

            // Find the column definition to get the selector function
            const columnDef = filteredColumnSchema.find(
                (col) => col.key === sortConfig.key,
            )

            console.log(
                '📊 Before sorting - First 3 items:',
                sorted.slice(0, 3).map((u) => {
                    let value
                    if (columnDef?.selector) {
                        value = columnDef.selector(u)
                    } else {
                        value = u[sortConfig.key as keyof typeof u]
                    }
                    return {
                        key: sortConfig.key,
                        value,
                        unit: u.unit_name,
                    }
                }),
            )

            sorted.sort((a, b) => {
                // Get values using the column's selector function if available
                let aValue: any
                let bValue: any

                if (columnDef?.selector) {
                    // Use the column's selector function (for workorder columns)
                    aValue = columnDef.selector(a)
                    bValue = columnDef.selector(b)
                } else {
                    // Use direct property access (for unit columns)
                    aValue = a[sortConfig.key as keyof typeof a]
                    bValue = b[sortConfig.key as keyof typeof b]
                }

                // Handle null/undefined values
                if (aValue == null && bValue == null) return 0
                if (aValue == null) return 1
                if (bValue == null) return -1

                // Convert to strings for comparison if needed
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase()
                    bValue = bValue.toLowerCase()
                }

                // Handle dates
                if (aValue instanceof Date && bValue instanceof Date) {
                    aValue = aValue.getTime()
                    bValue = bValue.getTime()
                }

                // Compare values
                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1
                }
                return 0
            })

            console.log(
                '✅ After sorting - First 3 items:',
                sorted.slice(0, 3).map((u) => {
                    let value
                    if (columnDef?.selector) {
                        value = columnDef.selector(u)
                    } else {
                        value = u[sortConfig.key as keyof typeof u]
                    }
                    return {
                        key: sortConfig.key,
                        value,
                        unit: u.unit_name,
                    }
                }),
            )
        }

        return sorted
    }, [filteredUnits, sortConfig, filteredColumnSchema])

    // Paginated data for performance (using sorted data when available)
    const paginatedUnits = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage
        const endIndex = startIndex + rowsPerPage

        // Use sorted data if available, otherwise use filtered data
        const dataToPaginate = sortedAndFilteredUnits || filteredUnits
        const paginated = dataToPaginate.slice(startIndex, endIndex)

        return paginated
    }, [sortedAndFilteredUnits, filteredUnits, currentPage, rowsPerPage])

    // Pagination controls
    const totalPages = Math.ceil(
        (sortedAndFilteredUnits || filteredUnits).length / rowsPerPage,
    )
    const handlePageChange = useCallback((newPage: number) => {
        setCurrentPage(newPage)
    }, [])

    const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage)
        setCurrentPage(1) // Reset to first page
    }, [])

    // Define primary headers
    const primaryHeaders = useMemo(
        () => [
            {
                name: 'Unit Info',
                startColumn: 0,
                endColumn: 1, // Location (0), Unit (1) - first two columns only
                style: {
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold' as const,
                    textAlign: 'center' as const,
                },
            },
            {
                name: 'Unit Details',
                startColumn: 2,
                endColumn: 4 + maxBedrooms, // Type (2), # Beds (3), + all bedroom columns (4 to 3+maxBedrooms), Services (4+maxBedrooms)
                style: {
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold' as const,
                    textAlign: 'center' as const,
                },
            },
            ...uniqueServices.map((service, index) => {
                // Calculate start column: after Unit Info section (4 + maxBedrooms + 1) + previous services
                // Each service has 6 columns (assigned_to, start_date, end_date, created_date, status, service_areas)
                // Plus 1 more column if cost is visible (SEE_VENDOR_FINANCES permission)
                const columnsPerService =
                    6 +
                    (hasPermission(workspaceUser, SEE_VENDOR_FINANCES) ? 1 : 0)
                const startCol = 5 + maxBedrooms + index * columnsPerService
                const endCol = startCol + columnsPerService - 1

                // Get the service color and icon from serviceMap, fallback to default if not found
                const serviceData = Object.values(serviceMap).find(
                    (s) => s.name === service,
                )
                const serviceColor = serviceData?.color || '#1976d2'
                const serviceIcon = getServiceImageDetails(
                    serviceData?.id ?? -1,
                    serviceData?.service_category,
                ).icon

                return {
                    name: (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                            }}
                        >
                            <img
                                src={serviceIcon}
                                style={{
                                    maxHeight: 20,
                                    maxWidth: 22,
                                    filter: 'brightness(0.8)',
                                }}
                                alt={service}
                            />
                            <span
                                style={{
                                    color: serviceColor,
                                    fontWeight: 'bold',
                                }}
                            >
                                {service}
                            </span>
                        </div>
                    ),
                    startColumn: startCol,
                    endColumn: endCol,
                    style: {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold' as const,
                        textAlign: 'center' as const,
                    },
                }
            }),
        ],
        [uniqueServices, maxBedrooms, serviceMap],
    )

    // Generate filtered primary headers based on visible columns
    const filteredPrimaryHeaders = useMemo(() => {
        if (hiddenColumns.size === 0) {
            // If no columns are hidden, use the original primary headers
            return primaryHeaders
        }

        const headers: any[] = []
        let currentColumnIndex = 0

        // Unit Info section (first two columns only)
        const unitInfoColumns = ['unit_location', 'unit_name']

        const visibleUnitInfoColumns = unitInfoColumns.filter(
            (col) => !hiddenColumns.has(col),
        )

        if (visibleUnitInfoColumns.length > 0) {
            headers.push({
                name: 'Unit Info',
                startColumn: currentColumnIndex,
                endColumn:
                    currentColumnIndex + visibleUnitInfoColumns.length - 1,
                style: {
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold' as const,
                    textAlign: 'center' as const,
                },
            })
            currentColumnIndex += visibleUnitInfoColumns.length
        }

        // Unit Details section (remaining unit columns)
        const unitDetailsColumns = [
            'unit_config_name',
            'occupiable_areas_count',
        ]

        // Add bedroom columns based on naming convention
        const bedroomColumns = Array.from({ length: maxBedrooms }, (_, i) => {
            if (bedroomNamingConvention === 'letters') {
                const bedLetter = String.fromCharCode(65 + i)
                return `bed_${bedLetter}_status_tag`
            } else {
                const bedNumber = String(i + 1)
                return `bed_${bedNumber}_status_tag`
            }
        })

        const allUnitDetailsColumns = [...unitDetailsColumns, ...bedroomColumns]

        // Include the Services column as part of Unit Details section
        if (!hiddenColumns.has('services')) {
            allUnitDetailsColumns.push('services')
        }

        const visibleUnitDetailsColumns = allUnitDetailsColumns.filter(
            (col) => !hiddenColumns.has(col),
        )

        if (visibleUnitDetailsColumns.length > 0) {
            headers.push({
                name: 'Unit Details',
                startColumn: currentColumnIndex,
                endColumn:
                    currentColumnIndex + visibleUnitDetailsColumns.length - 1,
                style: {
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold' as const,
                    textAlign: 'center' as const,
                },
            })
            currentColumnIndex += visibleUnitDetailsColumns.length
        }

        // Debug logging for Unit Info section
        console.log('Unit Info Section:', {
            unitInfoColumns,
            visibleUnitInfoColumns,
            currentColumnIndex,
        })

        // Service-specific columns
        uniqueServices.forEach((service) => {
            const serviceColumns = [
                `${service.toLowerCase()}_assigned_to`,
                `${service.toLowerCase()}_start_date`,
                `${service.toLowerCase()}_end_date`,
                `${service.toLowerCase()}_created_date`,
                `${service.toLowerCase()}_status`,
                `${service.toLowerCase()}_service_areas`,
            ]

            // Add cost column if user has permission
            if (hasPermission(workspaceUser, SEE_VENDOR_FINANCES)) {
                serviceColumns.push(`${service.toLowerCase()}_cost`)
            }

            const visibleServiceColumns = serviceColumns.filter(
                (col) => !hiddenColumns.has(col),
            )

            if (visibleServiceColumns.length > 0) {
                // Get the service color and icon
                const serviceData = Object.values(serviceMap).find(
                    (s) => s.name === service,
                )
                const serviceColor = serviceData?.color || '#1976d2'
                const serviceIcon = getServiceImageDetails(
                    serviceData?.id ?? -1,
                    serviceData?.service_category,
                ).icon

                headers.push({
                    name: (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                            }}
                        >
                            <img
                                src={serviceIcon}
                                style={{
                                    maxHeight: 20,
                                    maxWidth: 22,
                                    filter: 'brightness(0.8)',
                                }}
                                alt={service}
                            />
                            <span
                                style={{
                                    color: serviceColor,
                                    fontWeight: 'bold',
                                }}
                            >
                                {service}
                            </span>
                        </div>
                    ),
                    startColumn: currentColumnIndex,
                    endColumn:
                        currentColumnIndex + visibleServiceColumns.length - 1,
                    style: {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold' as const,
                        textAlign: 'center' as const,
                    },
                })
                currentColumnIndex += visibleServiceColumns.length
            }
        })

        // Debug logging
        console.log('Filtered Primary Headers:', {
            hiddenColumns: Array.from(hiddenColumns),
            totalHeaders: headers.length,
            headers: headers.map((h) => ({
                name: h.name,
                start: h.startColumn,
                end: h.endColumn,
            })),
        })

        return headers
    }, [
        primaryHeaders,
        hiddenColumns,
        maxBedrooms,
        bedroomNamingConvention,
        uniqueServices,
        serviceMap,
        workspaceUser,
    ])

    // Show loading state
    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <div style={{ marginBottom: '16px' }}>
                    <CircularProgress size={60} />
                </div>
                <div style={{ fontSize: '18px', color: '#666' }}>
                    Loading schedule data...
                </div>
            </div>
        )
    }

    // Show error state
    if (error) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                <div>Error: {error}</div>
                <button onClick={() => fetchUnits()}>Retry</button>
            </div>
        )
    }

    // Show empty state
    if (!units || units.length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>No units found for this schedule.</div>
                <button onClick={() => fetchUnits()}>Refresh</button>
            </div>
        )
    }

    return (
        <div>
            {/* Search Input */}
            <div style={{ padding: '2px', borderBottom: '1px solid #e0e0e0' }}>
                <div
                    style={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'center',
                        marginBottom: '12px',
                    }}
                >
                    {/* Toggle to Scheduler View Button */}
                    <Tooltip title="Switch to Scheduler View">
                        <IconButton
                            onClick={() => setIsSpreadsheetView(false)}
                            color="primary"
                            size="small"
                            style={{
                                backgroundColor: '#f0f8ff',
                            }}
                        >
                            <ViewModule />
                        </IconButton>
                    </Tooltip>

                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search units, locations, contractors, status..."
                        inputRef={searchInputRef}
                        onKeyPress={handleKeyPress}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />

                    {/* Apply Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApplySearch}
                        startIcon={<FilterList />}
                        size="small"
                    >
                        Apply
                    </Button>

                    {/* Clear Button */}
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            size="small"
                        >
                            Clear
                        </Button>
                    )}

                    {/* Bulk Edit Mode Button */}
                    <Tooltip title="Bulk Editor">
                        <IconButton
                            onClick={() => setBulkEditMode(!bulkEditMode)}
                            color={bulkEditMode ? 'primary' : 'default'}
                            size="small"
                        >
                            <Edit fontSize="large" />
                        </IconButton>
                    </Tooltip>

                    {schedule && schedule?.active && (
                        <Tooltip title="Assign">
                            <IconButton
                                onClick={() =>
                                    setAssignModalOpen(!assignModalOpen)
                                }
                                disabled={
                                    !hasPermission(
                                        workspaceUser,
                                        CREATE_WORKORDERS,
                                    )
                                }
                            >
                                <AddIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Column Visibility Button */}
                    <Tooltip title="Column Settings">
                        <IconButton
                            onClick={openColumnVisibilityDialog}
                            color="default"
                            style={{ position: 'relative' }}
                        >
                            <ViewColumn />
                            {hiddenColumns.size > 0 && (
                                <Chip
                                    label={hiddenColumns.size}
                                    size="small"
                                    style={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        fontSize: '10px',
                                        height: '16px',
                                        minWidth: '16px',
                                    }}
                                />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Filter">
                        <IconButton
                            onClick={() =>
                                setScheduleFilterOpen(!scheduleFilterOpen)
                            }
                        >
                            <TuneIcon
                                fontSize="large"
                                color={
                                    scheduleFilterOpen ? 'primary' : undefined
                                }
                            />
                        </IconButton>
                    </Tooltip>
                </div>

                <Divider />

                <Container
                    style={{
                        flex: 1,
                        paddingTop: theme.spacing(1),
                    }}
                >
                    <InfrastructureFilterChips
                        filter={filterController.filter}
                        locationSelection={locationFinderSelection.selection}
                        setLocationSelection={
                            locationFinderSelection.setFinderSelection
                        }
                        containerStyle={{ flex: 1 }}
                        hideInspectionChips
                        vendorList={vendorList}
                        unitConfigList={unitConfigList}
                        serviceList={serviceList}
                    />
                </Container>

                {/* Search Status */}
                {appliedSearchTerm && (
                    <div
                        style={{
                            fontSize: '14px',
                            color: '#666',
                            marginTop: '6px',
                        }}
                    >
                        Showing {filteredUnits.length} of {units.length} units
                        for: &ldquo;{appliedSearchTerm}&rdquo;
                    </div>
                )}

                {/* Table Title and Pagination Controls */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '12px',
                        padding: '12px 16px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                    }}
                >
                    {/* Table Title and Results Info */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                        }}
                    >
                        <div
                            style={{
                                fontSize: '18px',
                                color: '#212529',
                                fontWeight: 600,
                            }}
                        >
                            {schedule.name}
                        </div>
                        <div
                            style={{
                                fontSize: '14px',
                                color: '#495057',
                                fontWeight: 500,
                            }}
                        >
                            Showing{' '}
                            <strong>
                                {(currentPage - 1) * rowsPerPage + 1}
                            </strong>{' '}
                            to{' '}
                            <strong>
                                {Math.min(
                                    currentPage * rowsPerPage,
                                    filteredUnits.length,
                                )}
                            </strong>{' '}
                            of <strong>{filteredUnits.length}</strong> units
                        </div>
                    </div>

                    {/* Pagination Controls */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #dee2e6',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        }}
                    >
                        {/* Rows per page selector */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <span
                                style={{
                                    fontSize: '13px',
                                    color: '#6c757d',
                                    fontWeight: 500,
                                }}
                            >
                                Rows per page:
                            </span>
                            <select
                                value={rowsPerPage}
                                onChange={(e) =>
                                    handleRowsPerPageChange(
                                        Number(e.target.value),
                                    )
                                }
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    outline: 'none',
                                }}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </select>
                        </div>

                        {/* Divider */}
                        <div
                            style={{
                                width: '1px',
                                height: '24px',
                                backgroundColor: '#dee2e6',
                            }}
                        />

                        {/* Page navigation */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={currentPage === 1}
                                onClick={() =>
                                    handlePageChange(currentPage - 1)
                                }
                                style={{
                                    minWidth: '32px',
                                    padding: '6px 8px',
                                    borderColor:
                                        currentPage === 1
                                            ? '#e9ecef'
                                            : '#007bff',
                                    color:
                                        currentPage === 1
                                            ? '#adb5bd'
                                            : '#007bff',
                                }}
                            >
                                ‹
                            </Button>

                            <span
                                style={{
                                    fontSize: '13px',
                                    padding: '0 12px',
                                    color: '#495057',
                                    fontWeight: 500,
                                    minWidth: '80px',
                                    textAlign: 'center',
                                }}
                            >
                                Page <strong>{currentPage}</strong> of{' '}
                                <strong>{totalPages}</strong>
                            </span>

                            <Button
                                variant="outlined"
                                size="small"
                                disabled={currentPage === totalPages}
                                onClick={() =>
                                    handlePageChange(currentPage + 1)
                                }
                                style={{
                                    minWidth: '32px',
                                    padding: '6px 8px',
                                    borderColor:
                                        currentPage === totalPages
                                            ? '#e9ecef'
                                            : '#007bff',
                                    color:
                                        currentPage === totalPages
                                            ? '#adb5bd'
                                            : '#007bff',
                                }}
                            >
                                ›
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <SchedulerSpreadsheet
                rows={paginatedUnits}
                columns={filteredColumnSchema}
                primaryHeaders={filteredPrimaryHeaders}
                onSort={handleSort}
                sortConfig={sortConfig}
            />

            <ScheduleFilterDrawer
                open={scheduleFilterOpen}
                filter={filterController.filter}
                setInfrastructureFilter={
                    filterController.setInfrastructureFilter
                }
                onClose={() => setScheduleFilterOpen(false)}
                vendorList={vendorList}
                serviceList={serviceList}
                unitConfigList={unitConfigList}
                areaStatusConfigList={
                    aptConfigController.areaStatusConfigList ?? []
                }
                workorderList={workorderList ?? []}
                areaConfigMap={areaConfigMap}
                areaStatusTagMap={areaStatusTagMap}
                unitWorkorderMap={unitWorkorderMap}
                locationFinderSelection={locationFinderSelection}
                root={root}
                vacantAreas={vacantAreas}
                setVacantAreas={setVacantAreas}
                bulkSwapAreaStatusTag={bulkSwapAreaStatusTag}
                scheduleId={schedule.id}
            />

            <SchedulerAssignModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                filteredTree={prunedTree}
                serviceList={serviceList}
                unitConfigMap={unitConfigMap}
                unitWorkorderMap={unitWorkorderMap}
                // TODO: EZTK-280
                // areaLeaseMap={areaLeaseMap}
                areaLeaseMap={{}}
                schedule={schedule}
                areaConfigMap={areaConfigMap}
                vendorList={vendorList}
                serviceAreaMap={serviceAreaMap}
                createWorkorder={createWorkorder}
                assignmentFinderSelection={assignmentFinderSelection}
                isAreaInFilter={filterController.isAreaInFilter}
                areaStatusTagMap={areaStatusTagMap}
                areaStatusConfigList={
                    aptConfigController.areaStatusConfigList ?? []
                }
            />

            {/* Vendor Change Popover */}
            {selectedWorkorder && (
                <VendorChangePopover
                    open={vendorPopoverOpen}
                    anchorEl={vendorPopoverAnchor}
                    onClose={handleVendorPopoverClose}
                    workorder={selectedWorkorder}
                    vendorList={vendorList || []}
                    onVendorChange={(workorder, newVendorId) =>
                        handleVendorChange(workorder, newVendorId)
                    }
                />
            )}

            {/* Date Edit Popover */}
            {selectedDateWorkorder && (
                <DateEditPopover
                    open={datePopoverOpen}
                    anchorEl={datePopoverAnchor}
                    onClose={handleDatePopoverClose}
                    workorder={selectedDateWorkorder}
                    onDateChange={(workorder, startDate, endDate) =>
                        handleDateChange(workorder, startDate, endDate)
                    }
                />
            )}

            {/* Status Edit Popover */}
            {selectedStatusWorkorder && (
                <StatusEditPopover
                    open={statusPopoverOpen}
                    anchorEl={statusPopoverAnchor}
                    onClose={handleStatusPopoverClose}
                    workorder={selectedStatusWorkorder}
                    user={workspaceUser}
                    activeWorkspace={workspaceUser?.active_workspace}
                    onStatusTransition={handleStatusTransition}
                />
            )}

            {/* Cost Edit Popover */}
            {selectedCostWorkorder && (
                <CostEditPopover
                    open={costPopoverOpen}
                    anchorEl={costPopoverAnchor}
                    onClose={handleCostPopoverClose}
                    workorder={selectedCostWorkorder}
                    onCostChange={handleCostChange}
                />
            )}

            {/* Service Area Edit Popover */}
            {selectedServiceAreaWorkorder && (
                <ServiceAreaEditPopover
                    open={serviceAreaPopoverOpen}
                    anchorEl={serviceAreaPopoverAnchor}
                    onClose={handleServiceAreaPopoverClose}
                    workorder={selectedServiceAreaWorkorder}
                    areaConfigMap={areaConfigMap}
                    unitMap={
                        unitList?.reduce((acc, unit) => {
                            acc[unit.id] = unit
                            return acc
                        }, {} as Record<number, UnitRes>) || undefined
                    }
                    onServiceAreaChange={handleServiceAreaChange}
                    areaStatusTagMap={areaStatusTagMap}
                />
            )}

            {/* ServiceDots Popover */}
            {serviceDotsPopoverOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1000,
                    }}
                    onClick={handleServiceDotsPopoverClose}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: serviceDotsPopoverAnchor
                                ? serviceDotsPopoverAnchor.getBoundingClientRect()
                                      .bottom + 5
                                : 0,
                            left: serviceDotsPopoverAnchor
                                ? serviceDotsPopoverAnchor.getBoundingClientRect()
                                      .left
                                : 0,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '12px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            maxWidth: '300px',
                            zIndex: 1001,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{ fontWeight: 'bold', marginBottom: '8px' }}
                        >
                            Services
                        </div>
                        {selectedServiceDotsWorkorders.map(
                            (workorder, index) => (
                                <div
                                    key={index}
                                    style={{
                                        marginBottom: '4px',
                                        fontSize: '14px',
                                    }}
                                >
                                    <span style={{ fontWeight: 500 }}>
                                        {workorder.service_name}
                                    </span>
                                    {workorder.vendor_name && (
                                        <span
                                            style={{
                                                color: '#666',
                                                marginLeft: '8px',
                                            }}
                                        >
                                            - {workorder.vendor_name}
                                        </span>
                                    )}
                                    <span
                                        style={{
                                            color: '#666',
                                            marginLeft: '8px',
                                        }}
                                    >
                                        ({getUiStatus(workorder.status)})
                                    </span>
                                </div>
                            ),
                        )}
                    </div>
                </div>
            )}

            {/* Create Workorder Popover */}
            {selectedUnitForCreate && selectedServiceForCreate && (
                <CreateWorkorderPopover
                    open={createWorkorderPopoverOpen}
                    anchorEl={createWorkorderPopoverAnchor}
                    onClose={handleCreateWorkorderPopoverClose}
                    unit={selectedUnitForCreate}
                    service={selectedServiceForCreate}
                    vendorList={vendorList || []}
                    areaConfigMap={areaConfigMap}
                    unitMap={
                        unitList?.reduce((acc, unit) => {
                            acc[unit.id] = unit
                            return acc
                        }, {} as Record<number, UnitRes>) || undefined
                    }
                    onCreateWorkorder={createWorkorder}
                    scheduleId={schedule.id}
                    serviceAreaMap={serviceAreaMap}
                    areaLeaseMap={{}}
                    areaStatusTagMap={areaStatusTagMap}
                />
            )}

            <BulkEditSelectionList
                open={bulkEditMode}
                selectedWorkorders={selectedWorkorders}
                workordersInFilter={workorderList}
                vendorList={vendorList}
                handleClose={() => setBulkEditMode(false)}
                removeWorkorder={(id) => {
                    const newEditSelection = {
                        ...selectedWorkorders,
                    }
                    delete newEditSelection[id]
                    setSelectedWorkorders(newEditSelection)
                }}
                setSelectedWorkorders={setSelectedWorkorders}
                upStreamContractMap={upstreamContractMap}
                areaConfigMap={areaConfigMap}
                unitList={unitList ?? []}
                areaSelection={areaSelection}
                setAreaSelection={setAreaSelection}
                serviceList={serviceList}
                permissionRoles={permissionRoleList ?? []}
            />

            {/* Column Visibility Dialog */}
            <ColumnVisibilityModal
                open={columnVisibilityDialogOpen}
                onClose={closeColumnVisibilityDialog}
                hiddenColumns={hiddenColumns}
                toggleColumnVisibility={toggleColumnVisibility}
                showAllColumns={showAllColumns}
                filteredPrimaryHeaders={filteredPrimaryHeaders}
                filteredColumnSchema={filteredColumnSchema}
                maxBedrooms={maxBedrooms}
                uniqueServices={uniqueServices}
                serviceMap={serviceMap}
                workspaceUser={workspaceUser}
            />
        </div>
    )
}
